import React, { memo, ReactNode, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'

interface Props<V> {
  title: string
  subtitle?: string
  icon: ReactNode
  value: V
  selected?: boolean
  onClick(value: V): void
  dataTestId?: string
}

const lineClampStyles = tw`overflow-hidden [display: -webkit-box] [-webkit-line-clamp: 1] [line-clamp: 1] [-webkit-box-orient: vertical]`

/**
 * One tab of `TableTabs`. Ported from apps/dashboard's `TableTab` with its hex literals
 * expressed as this app's CSS-variable tokens: 236px wide, icon + title + count subtitle, and a
 * 3px primary bottom rule when selected.
 */
const TableTabComponent = <V extends string>({
  title,
  subtitle,
  icon,
  value,
  selected,
  onClick,
  dataTestId,
}: Props<V>) => {
  const handleClick = useCallback(() => {
    onClick(value)
  }, [value, onClick])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick(value)
      }
    },
    [value, onClick],
  )

  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={Boolean(selected)}
      data-testid={dataTestId}
      css={[
        // `min-w` rather than a fixed `w`: a tab grows to fit a long title instead of clamping it
        // to an ellipsis ("Partners scheduled…"), while a short one keeps the original 236px, so
        // existing callers with brief labels are unchanged. `shrink-0` stops the flex row
        // squeezing tabs back under that minimum once several are present.
        tw`flex min-w-[236px] shrink-0 cursor-pointer items-center border-0 border-r border-solid border-r-shades-100 border-b-shades-300 px-6 py-3 opacity-60 hover:border-b-[3px] hover:opacity-100`,
        selected &&
          tw`border-0 border-b-[3px] border-solid border-b-primary-600 bg-primary-100 opacity-100`,
      ]}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {icon}
      {/* Always two lines tall (20px title + 20px count), so every tab bar in the app has one
          height. A count tab keeps its line while the count is still '' — it no longer grows when
          the count lands after a load; a tab with no count at all centres its title instead. */}
      <div css={tw`ml-4 flex min-h-[40px] flex-col justify-center`}>
        <Typography
          variant="h6"
          css={[lineClampStyles, selected ? tw`text-primary-600` : tw`text-shades-1000`]}
        >
          {title}
        </Typography>
        {subtitle === undefined ? null : (
          <Typography
            variant="body2"
            css={[
              lineClampStyles,
              tw`min-h-[20px]`,
              selected ? tw`text-primary-500` : tw`text-shades-1000`,
            ]}
          >
            {subtitle}
          </Typography>
        )}
      </div>
    </div>
  )
}

export const TableTab = memo(TableTabComponent) as typeof TableTabComponent
